import { useState } from 'react';

import {
  AppIcon,
  AppIcons,
  BadgeIcon,
  BadgeIconProps,
  ContentNugget,
  ICON_SIZES,
  ActionTabs,
  ActionTabItem,
  HideOnRoute,
  useImageLoad,
  MeshGradient,
  resolveMeshConfig,
} from '@backrs_ui/common-ui';

import { Box, Button, Skeleton, SxProps, Theme, Typography } from '@mui/material';

const HEADER_IMAGE_HEIGHT = 200;
const LOGO_SIZE = 160;
const LOGO_SIZE_SMALL = 120;
const DEFAULT_BANNER_IMAGE_HEIGHT = 185;

type ResourceHeaderProps = Readonly<{
  title: string;
  titleExtra?: React.ReactNode;
  subtitle?: string;
  subtitleIcon?: AppIcons;
  titleIcon?: BadgeIconProps;
  banner_media_url?: string;
  /** When `banner_media_url` is missing, the header falls back to a per-variant mesh
   *  gradient resolved from this nugget. Omit to fall back to the generic blue. */
  nugget?: ContentNugget;
  logo_media_url?: string;
  logoShape?: 'circle' | 'rounded';
  city?: string;
  state?: string;
  country?: string;
  buttonMain?: ActionTabItem;
  buttonGroup: ActionTabItem[];
  headerActions?: React.ReactNode;
  headerActionsSxOverride?: SxProps<Theme>;
}>;

export const ResourceHeader = ({
  title,
  titleExtra,
  subtitle,
  subtitleIcon,
  titleIcon,
  banner_media_url,
  nugget,
  logo_media_url,
  logoShape = 'circle',
  city,
  state,
  country,
  buttonMain,
  buttonGroup,
  headerActions,
  headerActionsSxOverride,
}: ResourceHeaderProps) => {
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const banner = useImageLoad(banner_media_url);
  const logo = useImageLoad(logo_media_url);

  const showBanner = banner_media_url && !banner.imageError;

  const bannerMeshConfig = nugget ? resolveMeshConfig(nugget) : undefined;

  return (
    <Box sx={headerContainerSx(!!logo_media_url)}>
      {/* BANNER WITH ACTION BUTTONS */}
      <Box sx={bannerContainerSx}>
        {showBanner ? (
          <>
            {!banner.imageLoaded && (
              <Skeleton variant="rectangular" animation="wave" sx={bannerSkeletonSx} />
            )}
            <Box
              component="img"
              src={banner_media_url}
              alt=""
              sx={bannerImageWithLoadSx(banner.imageLoaded)}
              {...banner.imageProps}
            />
          </>
        ) : (
          <Box sx={defaultBannerImageSx(!!bannerMeshConfig)}>
            {bannerMeshConfig && <MeshGradient config={bannerMeshConfig} />}
          </Box>
        )}

        <HideOnRoute route="shared">
          <Box sx={bannerActionsSx}>
            <ActionTabs
              actions={buttonGroup}
              showCopySuccess={showCopySuccess}
              onCopySuccessChange={setShowCopySuccess}
            />
          </Box>
        </HideOnRoute>
      </Box>

      <Box sx={infoSx(!!logo_media_url)}>
        {logo_media_url && !logo.imageError && (
          <Box sx={logoWrapperSx}>
            {!logo.imageLoaded && (
              <Skeleton variant="rectangular" animation="wave" sx={logoSkeletonSx(logoShape)} />
            )}
            <Box
              component="img"
              src={logo_media_url}
              alt={title}
              sx={logoImageWithLoadSx(logoShape, logo.imageLoaded)}
              {...logo.imageProps}
            />
          </Box>
        )}
        {/* NAME AND LOCATION */}
        <Box sx={titleRowSx}>
          {titleIcon && <BadgeIcon {...titleIcon} />}
          <Box sx={titleSx}>
            <Box sx={titleWithExtraSx}>
              <Typography variant="display-xs-semibold">{title}</Typography>
              {titleExtra}
            </Box>
            {subtitle &&
              (subtitleIcon ? (
                <Box sx={subtitleWithIconSx}>
                  <AppIcon
                    iconName={subtitleIcon}
                    diameter="16px"
                    color="Colors/Foreground/fg-quaternary"
                  />
                  <Typography variant="text-sm-regular" sx={subtitleSx}>
                    {subtitle}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="text-sm-regular" sx={subtitleSx}>
                  {subtitle}
                </Typography>
              ))}
            {(city || state || country) && (
              <Box sx={locationSx}>
                <AppIcon
                  iconName="MarkerPin01"
                  diameter="16px"
                  color="Colors/Foreground/fg-quaternary"
                />
                <Typography variant="text-sm-regular">
                  {[city, state, country].filter(Boolean).join(', ')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={[headerActionsSx, headerActionsSxOverride ?? false] as SxProps<Theme>}>
          {headerActions}
          {buttonMain && (
            <Button
              id="resource-header-main-button"
              hierarchy="Secondary gray"
              size="md"
              icon="Leading"
              disabled={buttonMain.disabled}
              onClick={buttonMain.onClick}
              sx={buttonMainSx}
              startIcon={
                buttonMain.icon && (
                  <AppIcon
                    iconName={buttonMain.icon}
                    diameter="20px"
                    color="Colors/Text/text-secondary"
                  />
                )
              }
            >
              {buttonMain.label}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const headerContainerSx =
  (hasLogo: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'relative',
    mb: hasLogo
      ? `calc(${backrs.spacing['spacing-lg']} + ${backrs.spacing['spacing-lg']})`
      : backrs.spacing['spacing-lg'],
    '@container (min-width: 600px)': {
      mb: hasLogo
        ? `calc(${backrs.spacing['spacing-xl']} + ${backrs.spacing['spacing-4xl']})`
        : backrs.spacing['spacing-xl'],
    },
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  });

const bannerContainerSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
};

const bannerActionsSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-lg'],
  right: backrs.spacing['spacing-lg'],
  '@container (min-width: 600px)': {
    top: backrs.spacing['spacing-xl'],
    right: backrs.spacing['spacing-xl'],
  },
});

const bannerImageWithLoadSx =
  (loaded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '100%',
    height: `${HEADER_IMAGE_HEIGHT * 0.75}px`,
    '@container (min-width: 600px)': {
      height: `${HEADER_IMAGE_HEIGHT}px`,
    },
    '@media (max-height: 768px)': {
      height: `${HEADER_IMAGE_HEIGHT * 0.75}px`,
    },
    objectFit: 'cover',
    objectPosition: 'center',
    transition: 'height 0.3s ease, opacity 0.3s ease-in-out',
    background: 'transparent',
    borderRadius: backrs.radius['radius-xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    opacity: loaded ? 1 : 0,
  });

const bannerSkeletonSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  borderRadius: backrs.radius['radius-xl'],
});

const defaultBannerImageSx =
  (hasMeshConfig: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '100%',
    height: `${DEFAULT_BANNER_IMAGE_HEIGHT * 0.75}px`,
    '@container (min-width: 600px)': {
      height: `${DEFAULT_BANNER_IMAGE_HEIGHT}px`,
    },
    '@media (max-height: 768px)': {
      height: `${DEFAULT_BANNER_IMAGE_HEIGHT * 0.75}px`,
    },
    transition: 'height 0.3s ease',
    background: hasMeshConfig
      ? undefined
      : `linear-gradient(45deg, ${backrs.colors['Brand/900']} 0%, ${backrs.colors['Brand/600']} 100%)`,
    borderRadius: backrs.radius['radius-xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    position: 'relative',
    overflow: 'hidden',
  });

const logoWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  width: `${LOGO_SIZE_SMALL}px`,
  height: `${LOGO_SIZE_SMALL}px`,
  position: 'relative',
  top: `-${LOGO_SIZE_SMALL / 2}px`,
  marginBottom: `-${LOGO_SIZE_SMALL / 2}px`,
  left: 0,
  flexShrink: 0,
  '@container (min-width: 600px)': {
    width: `${LOGO_SIZE}px`,
    height: `${LOGO_SIZE}px`,
    position: 'absolute',
    top: `-${LOGO_SIZE / 2.25}px`,
    left: backrs.spacing['spacing-4xl'],
    marginBottom: 0,
  },
});

const logoImageWithLoadSx =
  (logoShape: 'circle' | 'rounded', loaded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'opacity 0.3s ease-in-out',
    borderRadius:
      logoShape === 'rounded' ? backrs.radius['radius-md'] : backrs.radius['radius-full'],
    border: `3px solid ${backrs.colors['Colors/Base/Black']}`,
    backgroundColor: backrs.colors['Colors/Base/White'],
    opacity: loaded ? 1 : 0,
  });

const logoSkeletonSx =
  (logoShape: 'circle' | 'rounded'): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    borderRadius:
      logoShape === 'rounded' ? backrs.radius['radius-md'] : backrs.radius['radius-full'],
    border: `3px solid ${backrs.colors['Colors/Base/Black']}`,
    backgroundColor: backrs.colors['Colors/Base/White'],
  });

const infoSx =
  (hasLogo: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'relative',
    mt: backrs.spacing['spacing-2xl'],
    px: 0,
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: backrs.spacing['spacing-lg'],
    boxSizing: 'border-box',
    '@container (min-width: 600px)': {
      px: 0,
      pl: hasLogo
        ? `calc(${LOGO_SIZE}px + ${backrs.spacing['spacing-5xl']} + ${backrs.spacing['spacing-2xl']})`
        : 0,
    },
  });

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  pt: backrs.spacing['spacing-lg'],
  pb: 0,
  width: '100%',
  '@container (min-width: 600px)': {
    pt: 0,
    pb: backrs.spacing['spacing-2xl'],
    width: 'auto',
  },
});

const titleSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const titleWithExtraSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const subtitleWithIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const locationSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  mt: backrs.spacing['spacing-xs'],
});

const headerActionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
  flexWrap: 'wrap',
});

const buttonMainSx: SxProps<Theme> = {
  height: ICON_SIZES['40px'],
};
